import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import SlotManager from "@/components/SlotManager";
import { MapPin, Zap, Euro, Check, X, MessageSquare } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { format } from "date-fns";
import { de } from "date-fns/locale";

const modeLabels: Record<string, string> = {
  instant: "Sofort verfügbar",
  request: "Auf Anfrage",
  reservable: "Reservierbar",
};

const HostDashboard = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [selectedSpotId, setSelectedSpotId] = useState<string | null>(null);

  const { data: spots, isLoading } = useQuery({
    queryKey: ["my_spots", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("charging_spots")
        .select("*")
        .eq("host_id", user!.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });

  const { data: bookings } = useQuery({
    queryKey: ["host_bookings", user?.id],
    queryFn: async () => {
      const spotIds = spots?.map((s) => s.id) ?? [];
      if (spotIds.length === 0) return [];
      const { data, error } = await supabase
        .from("bookings")
        .select("*, availability_slots(*), charging_spots(title)")
        .in("charging_spot_id", spotIds)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: !!spots && spots.length > 0,
  });

  const { data: requests } = useQuery({
    queryKey: ["host_requests", user?.id],
    queryFn: async () => {
      const spotIds = spots?.map((s) => s.id) ?? [];
      if (spotIds.length === 0) return [];
      const { data, error } = await supabase
        .from("booking_requests")
        .select("*, charging_spots(title)")
        .in("charging_spot_id", spotIds)
        .eq("status", "pending")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: !!spots && spots.length > 0,
  });

  const updateMode = useMutation({
    mutationFn: async ({ spotId, mode }: { spotId: string; mode: string }) => {
      const { error } = await supabase
        .from("charging_spots")
        .update({ availability_mode: mode } as any)
        .eq("id", spotId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["my_spots"] });
      toast.success("Modus aktualisiert");
    },
  });

  const respondRequest = useMutation({
    mutationFn: async ({ requestId, status }: { requestId: string; status: string }) => {
      const { error } = await supabase
        .from("booking_requests")
        .update({ status, responded_at: new Date().toISOString() })
        .eq("id", requestId);
      if (error) throw error;
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ["host_requests"] });
      toast.success(vars.status === "accepted" ? "Anfrage angenommen" : "Anfrage abgelehnt");
    },
  });

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Host-Dashboard</h1>
        <p className="text-sm text-muted-foreground">Verwalten Sie Ihre Ladepunkte und Buchungen</p>
      </div>

      {/* Requests */}
      {requests && requests.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-primary" />
            Offene Anfragen ({requests.length})
          </h2>
          {requests.map((r: any) => (
            <div key={r.id} className="rounded-xl border border-primary/20 bg-primary/5 p-4 space-y-3">
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-medium text-foreground">{r.charging_spots?.title}</p>
                  <p className="text-sm text-muted-foreground">
                    {format(new Date(r.created_at), "dd.MM.yyyy HH:mm", { locale: de })}
                  </p>
                  {r.message && <p className="mt-1 text-sm text-muted-foreground italic">"{r.message}"</p>}
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => respondRequest.mutate({ requestId: r.id, status: "accepted" })}
                    className="gap-1"
                  >
                    <Check className="h-3.5 w-3.5" /> Annehmen
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => respondRequest.mutate({ requestId: r.id, status: "rejected" })}
                    className="gap-1"
                  >
                    <X className="h-3.5 w-3.5" /> Ablehnen
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Spots */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-foreground">Meine Ladepunkte</h2>
        {isLoading && <p className="text-sm text-muted-foreground">Lädt…</p>}
        {spots?.length === 0 && <p className="text-sm text-muted-foreground">Noch keine Ladepunkte angelegt.</p>}
        {spots?.map((spot) => (
          <div key={spot.id} className="rounded-xl border border-border bg-card p-5 space-y-4">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-semibold text-foreground">{spot.title}</h3>
                <p className="flex items-center gap-1 text-sm text-muted-foreground">
                  <MapPin className="h-3 w-3" /> {spot.address}
                </p>
                <div className="mt-2 flex gap-3 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><Zap className="h-3 w-3" /> {spot.power_kw} kW</span>
                  <span className="flex items-center gap-1"><Euro className="h-3 w-3" /> {spot.price_per_hour.toFixed(2)} €/h</span>
                </div>
              </div>
              <div className="flex flex-col items-end gap-2">
                {spot.approved ? (
                  <Badge variant="secondary" className="bg-primary/10 text-primary">Freigegeben</Badge>
                ) : (
                  <Badge variant="secondary">Wartet auf Freigabe</Badge>
                )}
              </div>
            </div>

            {/* Mode selector */}
            <div className="flex items-center gap-3">
              <label className="text-sm font-medium text-muted-foreground whitespace-nowrap">Nutzungsmodus:</label>
              <Select
                value={(spot as any).availability_mode ?? "reservable"}
                onValueChange={(mode) => updateMode.mutate({ spotId: spot.id, mode })}
              >
                <SelectTrigger className="h-8 w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="instant">⚡ Sofort verfügbar</SelectItem>
                  <SelectItem value="request">💬 Auf Anfrage</SelectItem>
                  <SelectItem value="reservable">📅 Reservierbar</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {(spot as any).availability_mode === "reservable" || !(spot as any).availability_mode ? (
              <>
                <button
                  onClick={() => setSelectedSpotId(selectedSpotId === spot.id ? null : spot.id)}
                  className="text-sm font-medium text-primary hover:underline"
                >
                  {selectedSpotId === spot.id ? "Zeitfenster ausblenden" : "Zeitfenster verwalten"}
                </button>
                {selectedSpotId === spot.id && <SlotManager chargingSpotId={spot.id} />}
              </>
            ) : null}
          </div>
        ))}
      </div>

      {/* Bookings */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-foreground">Buchungen</h2>
        {bookings?.length === 0 && <p className="text-sm text-muted-foreground">Noch keine Buchungen.</p>}
        {bookings?.map((b) => (
          <div key={b.id} className="flex items-center justify-between rounded-lg border border-border bg-card px-4 py-3">
            <div className="text-sm">
              <span className="font-medium">{(b as any).charging_spots?.title ?? "Ladepunkt"}</span>
              <span className="ml-3 text-muted-foreground">{b.amount.toFixed(2)} €</span>
            </div>
            <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${
              b.status === "paid" ? "bg-primary/10 text-primary" :
              b.status === "cancelled" ? "bg-destructive/10 text-destructive" :
              "bg-muted text-muted-foreground"
            }`}>
              {b.status === "paid" ? "Bezahlt" : b.status === "cancelled" ? "Storniert" : "Ausstehend"}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default HostDashboard;
